/** @jsxImportSource preact */
import { AlertIcon } from "../icons";
import type { WidgetTokens } from "../theme";
import { Field } from "./base-ui.mjs";
import { FORM_STYLES, formVars } from "./form-styles";

export interface TextAreaFieldProps {
  tokens: WidgetTokens;
  label: string;
  name?: string;
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** 字段下方的提示。 */
  description?: string;
  /** 错误文字；有它就算无效，并替换提示。 */
  error?: string;
  invalid?: boolean;
  disabled?: boolean;
}

/** 多行输入框的样式：外观同 TextField 的输入井（浅灰底、无边框，聚焦变白加墨色环，无效是危险色环），最矮 100px；内容多了自己长高（field-sizing，不支持的浏览器里出滚动条），也可以手动竖向拉高。 */
export const TEXT_AREA_FIELD_STYLES = `
.gn-form-area { box-sizing:border-box; display:block; width:100%; min-height:100px; max-height:220px; field-sizing:content; padding:12px 14px; border:0; border-radius:var(--gn-form-r-input); background:var(--gn-form-sheet); color:var(--gn-form-ink); font:inherit; font-size:14px; line-height:1.45; resize:vertical; transition:background .15s, box-shadow .15s; }
.gn-form-area:focus { outline:none; background:var(--gn-form-sheet); box-shadow:0 0 0 2px var(--gn-form-ring); }
.gn-form-area[data-invalid] { box-shadow:0 0 0 1.5px var(--gn-form-danger); }
.gn-form-area:disabled { opacity:.55; cursor:not-allowed; resize:none; }
.gn-form-area::placeholder { color:var(--gn-form-faint); }
@media (prefers-reduced-motion: reduce) { .gn-form-area { transition:none; } }
`;

/**
 * 多行输入框：标签、提示和错误，外观同 TextField。底层是 Base UI 的 Field，
 * 标签与输入框、提示与错误的关联（for、aria-describedby、aria-invalid）都由它处理，
 * 输入框本身换成 textarea。
 */
export function TextAreaField({
  tokens: t,
  label,
  name,
  placeholder,
  value,
  defaultValue,
  onValueChange,
  description,
  error,
  invalid = false,
  disabled = false,
}: TextAreaFieldProps) {
  const isInvalid = invalid || Boolean(error);
  return (
    <Field.Root
      className="gn-form-field"
      disabled={disabled}
      invalid={isInvalid}
      name={name}
      style={formVars(t)}
    >
      <style>{`${FORM_STYLES}${TEXT_AREA_FIELD_STYLES}`}</style>
      <Field.Label className="gn-form-label">{label}</Field.Label>
      <Field.Control
        className="gn-form-area"
        defaultValue={defaultValue}
        onValueChange={(next) => onValueChange?.(next)}
        placeholder={placeholder}
        render={(props) => <textarea {...props} />}
        value={value}
      />
      {error ? (
        <Field.Error className="gn-form-error" match role="alert">
          <AlertIcon size={14} />
          <span>{error}</span>
        </Field.Error>
      ) : (
        description && (
          <Field.Description className="gn-form-hint">
            {description}
          </Field.Description>
        )
      )}
    </Field.Root>
  );
}
